import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ChevronLeft, CreditCard, FileCheck, Loader2, CheckCircle2, AlertTriangle, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useApp } from '@/context/AppContext';
import apiService from '@/services/apiService';
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics';

// PayPal SVG Icon
const PayPalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
  </svg>
);

const PaymentCheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { visaType } = location.state || {};
  const { trackEvent } = useAnalytics();
  
  const { 
    uploadQueue, 
    queuePricing, 
    clearQueue, 
    removeFromQueue,
    stripeMockMode,
    getPendingIds
  } = useApp();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('paypal'); // 'paypal' or 'stripe'
  const processingRef = useRef(false);

  useEffect(() => {
    if (uploadQueue.length === 0) {
      navigate('/documents', { state: { visaType } });
    }
  }, [uploadQueue, navigate, visaType]);

  const handlePayment = useCallback(async () => {
    if (isProcessing || processingRef.current) return;
    processingRef.current = true;
    setIsProcessing(true);
    setError(null);
    
    // Track payment started
    trackEvent(ANALYTICS_EVENTS.PAYMENT_STARTED, {
      document_count: uploadQueue.length,
      total_amount: queuePricing.total,
      mock_mode: stripeMockMode,
      payment_method: paymentMethod,
      visa_type: visaType
    });
    
    try {
      const pendingIds = getPendingIds();
      
      if (pendingIds.length === 0) {
        throw new Error('No documents in queue. Please upload documents first.');
      }
      
      // Try to refresh expiration times before payment
      setProcessingStep('Preparing documents...');
      try {
        await apiService.refreshPendingDocuments(pendingIds);
      } catch (refreshError) {
        // If refresh fails, the documents may have expired
        console.error('Failed to refresh pending documents:', refreshError);
        clearQueue();
        throw new Error('Your uploaded documents have expired. Please upload them again.');
      }
      
      // Store pending IDs for after payment
      localStorage.setItem('pending_document_ids', JSON.stringify(pendingIds));
      localStorage.setItem('batch_visa_type', visaType || '');
      
      if (stripeMockMode) {
        // Mock mode - process directly without payment
        setProcessingStep('Processing documents (Test Mode)...');
        
        const result = await apiService.processPendingDocumentsBatchMock(pendingIds);
        
        // Clear queue and navigate to results
        clearQueue();
        localStorage.removeItem('pending_document_ids');
        
        navigate('/batch-results', {
          state: {
            batchResult: result,
            visaType
          }
        });
      } else if (paymentMethod === 'paypal') {
        // PayPal payment
        setProcessingStep('Creating PayPal order...');
        
        try {
          const order = await apiService.createPayPalOrder(uploadQueue.length, false);
          
          console.log('PayPal order response:', order);
          
          if (order && order.approval_url) {
            // Store order for verification after payment
            localStorage.setItem('paypal_order_id', order.order_id);
            localStorage.setItem('payment_provider', 'paypal');
            
            // Redirect to PayPal
            setProcessingStep('Redirecting to PayPal...');
            window.location.replace(order.approval_url);
            return;
          } else {
            console.error('No approval URL in response:', order);
            throw new Error('Could not create PayPal order - no approval URL received');
          }
        } catch (paypalError) {
          console.error('PayPal order creation error:', paypalError);
          throw paypalError;
        }
      } else {
        // Stripe payment
        setProcessingStep('Creating payment session...');
        
        try {
          const session = await apiService.createCheckoutSession(null, uploadQueue.length);
          
          console.log('Checkout session response:', session);
          
          if (session && session.checkout_url) {
            // Store session for verification after payment
            localStorage.setItem('stripe_session_id', session.session_id);
            localStorage.setItem('payment_provider', 'stripe');
            
            // Redirect to Stripe
            setProcessingStep('Redirecting to payment...');
            window.location.replace(session.checkout_url);
            return;
          } else {
            console.error('No checkout URL in response:', session);
            throw new Error('Could not create payment session - no checkout URL received');
          }
        } catch (checkoutError) {
          console.error('Checkout creation error:', checkoutError);
          throw checkoutError;
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || err.message || 'Payment failed');
      setIsProcessing(false);
      processingRef.current = false;
    }
  }, [isProcessing, uploadQueue, stripeMockMode, paymentMethod, visaType, clearQueue, getPendingIds, navigate, queuePricing.total, trackEvent]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/documents', { state: { visaType } })}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Back to Documents</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <div className="w-24"></div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <h1 className="font-outfit font-bold text-3xl text-slate-900 mb-2">
              Complete Your Payment
            </h1>
            <p className="font-public text-slate-600">
              {visaType || 'Document Analysis'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-public font-semibold text-destructive">Payment Error</p>
                <p className="text-sm text-destructive/80">{error}</p>
              </div>
            </motion.div>
          )}

          {/* Order Summary */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
            <h2 className="font-outfit font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-trust" />
              Order Summary
            </h2>
            
            {/* Document List */}
            <div className="space-y-3 mb-6">
              {uploadQueue.map((doc, idx) => (
                <div
                  key={doc.pendingId}
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-verify" />
                    <div>
                      <p className="font-public text-sm text-slate-900">{doc.documentType}</p>
                      <p className="text-xs text-slate-500">
                        {doc.filename} {formatFileSize(doc.fileSize)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromQueue(doc.pendingId)}
                    className="p-1 text-slate-400 hover:text-destructive transition-colors"
                    data-testid={`remove-doc-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Pricing */}
            <div className="border-t border-slate-200 pt-4">
              <div className="flex justify-between text-sm text-slate-600 mb-2">
                <span>{queuePricing.documentCount} document{queuePricing.documentCount > 1 ? 's' : ''} × ${queuePricing.perDocument}</span>
                <span>${queuePricing.documentCount * queuePricing.perDocument}</span>
              </div>
              {queuePricing.discount > 0 && (
                <div className="flex justify-between text-sm text-verify mb-2">
                  <span>Bundle discount ({queuePricing.discount}%)</span>
                  <span>-${queuePricing.savings}</span>
                </div>
              )}
              <div className="flex justify-between font-outfit font-bold text-lg text-slate-900 pt-2 border-t border-slate-200">
                <span>Total</span>
                <span>${queuePricing.total}</span>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          {!stripeMockMode && (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
              <h2 className="font-outfit font-bold text-lg text-slate-900 mb-4">
                Payment Method
              </h2>
              
              <div className="space-y-3">
                {/* PayPal Option */}
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`w-full p-4 rounded-lg border-2 transition-all flex items-center gap-4 ${
                    paymentMethod === 'paypal'
                      ? 'border-[#0070ba] bg-[#0070ba]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="paypal-option"
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'paypal' ? 'border-[#0070ba]' : 'border-slate-300'
                  }`}>
                    {paymentMethod === 'paypal' && (
                      <div className="w-3 h-3 rounded-full bg-[#0070ba]" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <PayPalIcon className="w-6 h-6 text-[#0070ba]" />
                    <span className="font-public font-semibold text-slate-900">PayPal</span>
                  </div>
                  <span className="text-sm text-slate-500">Recommended</span>
                </button>

                {/* Stripe/Card Option */}
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={`w-full p-4 rounded-lg border-2 transition-all flex items-center gap-4 ${
                    paymentMethod === 'stripe'
                      ? 'border-trust bg-trust/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="stripe-option"
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'stripe' ? 'border-trust' : 'border-slate-300'
                  }`}>
                    {paymentMethod === 'stripe' && (
                      <div className="w-3 h-3 rounded-full bg-trust" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <CreditCard className="w-6 h-6 text-slate-600" />
                    <span className="font-public font-semibold text-slate-900">Credit/Debit Card</span>
                  </div>
                  <span className="text-sm text-slate-400">via Stripe</span>
                </button>
              </div>
            </div>
          )}

          {/* Pay Button */}
          <motion.button
            onClick={handlePayment}
            disabled={isProcessing || uploadQueue.length === 0}
            className={`w-full py-4 rounded-lg font-public font-semibold text-lg transition-all flex items-center justify-center gap-3 ${
              paymentMethod === 'paypal' && !stripeMockMode
                ? 'bg-[#0070ba] hover:bg-[#005ea6] text-white'
                : 'bg-trust hover:bg-trust/90 text-trust-foreground'
            } ${isProcessing || uploadQueue.length === 0 ? 'opacity-50 cursor-not-allowed' : 'shadow-md hover:shadow-lg'}`}
            whileTap={{ scale: isProcessing ? 1 : 0.98 }}
            data-testid="pay-button"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {processingStep}
              </>
            ) : stripeMockMode ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Analyze Documents (Test Mode)
              </>
            ) : paymentMethod === 'paypal' ? (
              <>
                <PayPalIcon className="w-5 h-5" />
                Pay ${queuePricing.total} with PayPal
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                Pay ${queuePricing.total} with Card
              </>
            )}
          </motion.button>

          {/* Security Notice */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500">
            <Shield className="w-4 h-4" />
            <span>Secure payment powered by {paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'}</span>
          </div>

          {/* Test Mode Notice */}
          {stripeMockMode && (
            <div className="mt-4 bg-warning/10 border border-warning/20 rounded-lg p-4 text-center">
              <p className="text-sm text-warning font-medium">
                Test Mode Active - No payment required
              </p>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="payment-disclaimer">
            <p className="text-xs text-slate-500 text-center">
              <strong>Important:</strong> This service identifies common document issues and potential risks. 
              It does not provide legal advice, official certification, or guarantee visa approval. 
              Results are for informational purposes only.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentCheckoutPage;
